import Navbar from "@/components/common/Navbar";
import Footer from "@/components/common/Footer";
import ChatbotModal from "@/components/common/ChatbotModal";
import React, { ReactNode, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import AuthModal from "@/components/common/AuthModal";
import { useTheme } from "@/contexts/ThemeContext";
import DynamicFavicon from "@/components/common/DynamicFavicon";
import { useLocation } from "wouter";
import { SiteVideoBackdrop } from "@/components/common/SiteVideoBackdrop";
import MobileContactBar, { MOBILE_CONTACT_BAR_OFFSET } from "@/components/common/MobileContactBar";
import { useSingleH1Guard } from "@/hooks/useSingleH1Guard";
import { useBrandingSettings } from "@/contexts/AppSettingsContext";
import {
  useCustomSiteBackdrop,
  SITE_VIDEO_BACKDROP_CLASS,
  applySiteVideoBackdropTransparent,
  clearSiteVideoBackdropTransparent,
} from "@/hooks/useCustomSiteBackdrop";
import { cn } from "@/lib/utils";

interface MainLayoutProps {
  children: ReactNode;
}

function MainLayout({ children }: MainLayoutProps) {
  useSingleH1Guard();
  const { theme } = useTheme();
  const { isActive: siteVideoBackdrop } = useCustomSiteBackdrop();
  const { contactPhone } = useBrandingSettings();
  const showMobileContactBar = Boolean(contactPhone?.trim());
  const [location] = useLocation();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalType, setAuthModalType] = useState<'login' | 'register'>('login');
  const [isChatbotModalOpen, setIsChatbotModalOpen] = useState(false);

  const openLoginModal = () => {
    setAuthModalType('login');
    setIsAuthModalOpen(true);
  };

  const openRegisterModal = () => {
    setAuthModalType('register');
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  const openChatbotModal = () => {
    setIsChatbotModalOpen(true);
  };

  const closeChatbotModal = () => {
    setIsChatbotModalOpen(false);
  };

  const handleAppointmentComplete = (appointmentData: unknown) => {
    console.log('Appointment completed:', appointmentData);
    setTimeout(() => {
      closeChatbotModal();
    }, 2000);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location]);

  useEffect(() => {
    if (!siteVideoBackdrop) {
      document.body.classList.remove(SITE_VIDEO_BACKDROP_CLASS);
      document.body.classList.remove('home-fixed-video-backdrop');
      clearSiteVideoBackdropTransparent();
      return;
    }

    document.body.classList.add(SITE_VIDEO_BACKDROP_CLASS);
    document.body.classList.remove('home-fixed-video-backdrop');
    applySiteVideoBackdropTransparent();

    return () => {
      document.body.classList.remove(SITE_VIDEO_BACKDROP_CLASS);
      clearSiteVideoBackdropTransparent();
    };
  }, [siteVideoBackdrop]);

  /** Hero remounts on SPA navigation — re-apply transparent pe banner nou */
  useEffect(() => {
    if (!siteVideoBackdrop) return;
    applySiteVideoBackdropTransparent();
    const raf = requestAnimationFrame(applySiteVideoBackdropTransparent);
    return () => cancelAnimationFrame(raf);
  }, [siteVideoBackdrop, location]);

  useEffect(() => {
    const handleOpenLoginModal = () => {
      openLoginModal();
    };

    window.addEventListener('open-login-modal', handleOpenLoginModal);

    return () => {
      window.removeEventListener('open-login-modal', handleOpenLoginModal);
    };
  }, []);

  const videoPortal =
    siteVideoBackdrop && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
            aria-hidden
            data-site-video-backdrop
          >
            <SiteVideoBackdrop />
          </div>,
          document.body
        )
      : null;

  return (
    <div
      className={cn(
        'flex flex-col min-h-screen',
        showMobileContactBar && MOBILE_CONTACT_BAR_OFFSET
      )}
      style={{
        backgroundColor: siteVideoBackdrop ? 'transparent' : '#ffffff',
        fontFamily: theme?.font_family,
      }}
    >
      {videoPortal}
      <DynamicFavicon />
      <Navbar
        onLogin={openLoginModal}
        onRegister={openRegisterModal}
        onAppointmentChat={openChatbotModal}
      />
      <main
        className="relative z-10 flex-grow"
        style={
          siteVideoBackdrop
            ? { backgroundColor: 'transparent', color: theme?.text_color }
            : {
                backgroundColor: 'var(--theme-bg-color, #F9FAFB)',
                color: 'var(--theme-text-color, #1E3A8A)',
              }
        }
      >
        {children}
      </main>
      <div className="relative z-10 flex-shrink-0">
        <Footer />
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        type={authModalType}
        onSwitchType={() => setAuthModalType(authModalType === 'login' ? 'register' : 'login')}
      />

      <ChatbotModal
        isOpen={isChatbotModalOpen}
        onClose={closeChatbotModal}
        onAppointmentComplete={handleAppointmentComplete}
      />

      <MobileContactBar />
    </div>
  );
}

export default React.memo(MainLayout);
